<template>
    <div v-if="user">
        <div class="col-md-6" v-bind:class="{'col-md-6': canAssignModules, 'col-md-12': ! canAssignModules}">
            <email-search
                v-if="mode === 'create'"
                v-model="user.email"
                :context="context"
                @user-select="selectUser"
            >
            </email-search>
            <div v-else class="form-group">
                <label for="name">Primary email address</label>
                <input v-model="user.email" class="form-control" :disabled="mode !== 'create'"/>
            </div>
            <div class="form-group">
                <label for="additional_emails">Secondary email addresses</label>
                <additional-emails
                    v-model="user.additional_emails"
                    :user-id="user.id"
                    @make-email-primary="makeEmailPrimary"
                    @validate="setAdditionalEmailsAsValid"
                >
                </additional-emails>
            </div>
            <div class="form-group">
                <label for="name">Name</label> <span class="badge-req badge badge-danger">Required</span>
                <input v-model="user.name" class="form-control"/>
            </div>
            
            <template v-if="mode === 'manage'">
                <base-password-input 
                    class="form-group" 
                    :class="{'has-error': fieldHasErrors('password')}" 
                    v-model="user.password" 
                    label="Password"
                    name="password">
                </base-password-input>

                <base-password-input 
                    class="form-group" 
                    :class="{'has-error': fieldHasErrors('password')}" 
                    label="Repeat password"
                    v-model="user.password_confirmation" 
                    name="password_confirmation">
                </base-password-input>

                <a href="javascript:void(0);" data-toggle="collapse" data-target="#collapsePasswordRules" aria-expanded="false" aria-controls="collapsePasswordRules">Passwords rules</a>
                <div id="collapsePasswordRules" class="collapse">
                    <div class="alert alert-default mt-4">
                        <h5>Passwords must:</h5>
                        <ul>
                            <li>Be at least 10 characters long.</li>
                            <li>Contain at least one lower case charcter (a-z)</li>
                            <li>Contain at least one uppercase charcter (A-Z)</li>
                            <li>Contain at least one number (0-9)</li>
                            <li>Contain at least one special charcter (!@#$%^&*()_+|~-=\`{}[]:";'<>?,./)</li>
                            <li>Not include spaces</li>
                            <li>Not be a known <a href="https://haveibeenpwned.com/Passwords" target="_blank">Pwned password</a></li>
                        </ul>
                    </div>
                </div>
            </template>

            <div class="form-group" v-if="mode === 'create'">
                <label for="personal_message">Personal message</label>
                <textarea v-model="user.personal_message" class="form-control" maxlength="65535">
                </textarea>
            </div>
            <div class="form-group" v-if="canAssignRole">
                <label for="name" class="control-label">Role</label> <span class="badge-req badge badge-danger">Required</span>
                <select v-model="user.role" name="role" class="form-control" id="role" required>
                    <option selected disabled>Select a role...</option>
                    <template>
                        <option value="staff">Staff</option>
                        <option value="approver">Approver</option>
                        <option value="project_owner">Project Owner</option>
                    </template>
                    <template v-if="canAssignAdmin">
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                        <option value="organisation">Organisation</option>
                    </template>
                    <template v-else-if="isDataRoomUser">
                        <option :value="user.role">Dataroom user</option>
                    </template>

                </select>
            </div>
            <div class="form-group" v-if="mode === 'edit'">
                <template v-if="user.activated">
                    <label for="reset_link">Resend invite</label><br>
                    <a href="javascript:;" @click.prevent="resendInvite" class="btn btn-default waves-effect waves-light">Resend invite email</a>
                </template>
                <template v-else>
                    <label for="reset_link">Reset password</label><br>
                    <a href="javascript:;" @click.prevent="resetPassword" class="btn btn-default waves-effect waves-light">{{user.password_email_is_pending ? 'Resend password reset email' : 'Send password reset email'}}</a>
                </template>
            </div>

            <div class="form-group">
                <label for="local">Locale</label>
                <select class="form-control" v-model="user.config.locale">
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                </select>
            </div>
        </div>
        <div v-if="canAssignModules" class="col-md-6">
            <div v-if="user.role === 'admin'" class="alert alert-info" id="admin-notification">
                Admin users have access to all projects.
            </div>
            <div v-else-if="staff_roles.includes(user.role) && availableModules.length > 1" class="form-group" id="project-list">
                <label>Projects</label> <span class="badge-req badge badge-danger">Required</span>
                <div class="btn-group pull-right" role="group" aria-label="..." style="margin-top: -5px">
                    <a href="javascript:;" @click="selectAllModules()" class="btn btn-xs btn-primary select-all-projects">Select all</a>
                    <a href="javascript:;" @click="deselectAllModules()" class="btn btn-xs btn-primary deselect-all-projects">Deselect all</a>
                </div>
                <div class="panel-group" id="accordion" role="tablist" aria-multiselectable="true">
                    <div v-for="project in availableModules" :key="project.id" class="panel" v-bind:class="[isProjectSelected(project) ? 'panel-success' : 'panel-default']" :data-project="project.id">
                        <div class="panel-heading" role="tab" :id="`heading-${project.id}`">
                            <h4 class="panel-title">
                                <a role="button" class="collapsed" data-toggle="collapse" data-parent="#accordion" :href="'#collapse-'+project.id" aria-expanded="false" :aria-controls="'collapse-'+project.id">
                                    {{ project.name }}
                                </a>
                            </h4>
                        </div>
                        <div :id="'collapse-'+project.id" class="panel-collapse collapse" role="tabpanel" aria-labelledby="'heading-'+project.id">
                            <div class="panel-body">
                                <div class="btn-group pull-right" role="group" aria-label="...">
                                    <a href="javascript:;" @click="selectProjectModules(project)" class="btn btn-xs btn-default select-all">Select all</a>
                                    <a href="javascript:;" @click="deselectProjectModules(project)" class="btn btn-xs btn-default deselect-all">Deselect all</a>
                                </div>
                                <ul v-if="project.modules.data.length" class="list-unstyled">
                                    <li v-for="parentModule in project.modules.data" :key="parentModule.id">
                                        <label>
                                            <input type="checkbox" v-model="user.modules[project.id]" :value="parentModule.id" @change="changeModuleParent($event, project.id, parentModule)" class="parent-module" :data-project="project.id" :data-id="parentModule.id"/>
                                            {{ parentModule.name }}
                                        </label>
                                        <ul v-if="parentModule.submodules.data.length">
                                            <li v-for="submodule in parentModule.submodules.data" :key="submodule.id" style="list-style: none">
                                                <label>
                                                    <input type="checkbox" v-model="user.modules[project.id]" :value="submodule.id" @change="changeModuleChild($event, project.id, parentModule)" :data-project="project.id" :data-parent="parentModule.id" class="child-module"/>
                                                    {{ submodule.name }}
                                                </label>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else-if="staff_roles.includes(user.role) && availableModules.length > 0" class="form-group" id="project-list">
                <label>Project Access</label> <span class="badge-req badge badge-danger">Required</span>
                <div class="btn-group pull-right" role="group" aria-label="...">
                    <a href="javascript:;" @click="selectProjectModules(availableModules[0])" class="btn btn-xs btn-default select-all">Select all</a>
                    <a href="javascript:;" @click="deselectProjectModules(availableModules[0])" class="btn btn-xs btn-default deselect-all">Deselect all</a>
                </div>
                <ul v-if="availableModules[0].modules.data.length" class="list-unstyled">
                    <li v-for="parentModule in availableModules[0].modules.data" :key="parentModule.id">
                        <label>
                            <input type="checkbox" v-model="user.modules[availableModules[0].id]" :value="parentModule.id" @change="changeModuleParent($event, availableModules[0].id, parentModule)" class="parent-module" :data-project="availableModules[0].id" :data-id="parentModule.id"/>
                            {{ parentModule.name }}
                        </label>
                        <ul v-if="parentModule.submodules.data.length">
                            <li v-for="submodule in parentModule.submodules.data" :key="submodule.id" style="list-style: none">
                                <label>
                                    <input type="checkbox" v-model="user.modules[availableModules[0].id]" :value="submodule.id" @change="changeModuleChild($event, availableModules[0].id, parentModule)" :data-project="availableModules[0].id" :data-parent="parentModule.id" class="child-module"/>
                                    {{ submodule.name }}
                                </label>
                            </li>
                        </ul>
                    </li>
                </ul>
            </div>
        </div>
    </div>
    <spinner v-else :show="true"/>
</template>

<script>
import VueI18n from 'vue-i18n';
import { startCase, union, has } from 'lodash';
import { User } from 'ca-api-client';
import AdditionalEmails from './additional-emails';
import EmailSearch from './email-search.vue';
import Spinner from '@/components/media/Spinner';
import BasePasswordInput from '@/components/base/BasePasswordInput';

// language bundle
import { i18n } from '@/i18n';

const STAFF_ROLLS = [
    'staff',
    'approver',
    'project_owner'
];

export default {
    components: {
        EmailSearch,
        AdditionalEmails,
        Spinner,
        BasePasswordInput,
    },
    i18n,
    name: 'UserForm',
    props: {
        value: Object,
        mode: String,
        editorRole: { type: String, default: null },
        availableModules: Array,
        context: String,
        errors: Object,
    },
    data() {
        return {
            additionalEmailsAreValid: true,
            isDataRoomUser: false,
            staff_roles: STAFF_ROLLS,
        };
    },
    computed: {
        user: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value)
            }
        },
        canAssignRole() {
            if (this.mode === 'manage') {
                return false;
            }
            if (this.context === 'super') {
                return false;
            }

            if (this.editorRole === 'admin') {
                return true;
            }

            if (this.editorRole === 'staff') {
                if (this.isDataRoomUser) {
                    return true;
                }

                if (! this.user.id) {
                    return true;
                }
            }

            return false;
        },
        canAssignAdmin() {
            return this.mode !== 'manage' && this.editorRole === 'admin';
        },
        canAssignModules() {
            if (this.mode === 'manage') {
                return false;
            }

            if (this.context === 'super') {
                return false;
            }

            if (this.isDataRoomUser) {
                return false;
            }

            return this.availableModules ? this.availableModules.length > 0 : false;
        },
        isValid() {
            if (!this.user) {
                return false;
            }

            if (!this.user.email) {
                return false;
            }

            if (!this.user.name) {
                return false;
            }

            if (this.canAssignRole && !this.user.role) {
                return false;
            }

            if (!this.additionalEmailsAreValid) {
                return false;
            }

            if (this.canAssignModules && this.user.role === 'staff' &&
                Object.values(this.user.modules).every((modules) => {
                    return modules.length === 0;
                })) {

                return false;
            }

            return true;
        },
    },
    methods: {
        makeEmailPrimary(email) {
            this.user.email = email.address;
        },
        changeModuleParent(event, projectId, module) {
            if (event.target.checked) {
                this.user.modules[projectId] = union(
                    module.submodules.data.map(submodule => {
                        return submodule.id
                    }).concat(module.id),
                    this.user.modules[projectId]
                );
            } else {
                this.user.modules[projectId] = this.user.modules[projectId].filter(currentModule => {
                    return currentModule !== module.id && !module.submodules.data.some(submodule => {
                        return submodule.id == currentModule;
                    });
                })
            }
        },
        changeModuleChild(event, projectId, parentModule) {
            if (event.target.checked) {
                this.user.modules[projectId] = union(
                    this.user.modules[projectId],
                    [parentModule.id]
                );
            } else {
                const anyChildren = this.user.modules[projectId].some(currentModule => {
                    return parentModule.submodules.data.some(submodule => {
                        return currentModule === submodule.id;
                    });
                });

                if (! anyChildren) {
                    const index = this.user.modules[projectId].indexOf(parentModule.id);
                    this.user.modules[projectId].splice(index, 1);
                }
            }
        },
        selectProjectModules(project) {
            this.$set(this.user.modules, project.id, []);

            project.modules.data.forEach(module => {
                this.user.modules[project.id].push(module.id);

                module.submodules.data.forEach(submodule => {
                    this.user.modules[project.id].push(submodule.id);

                });
            });
        },
        deselectProjectModules(project) {
            this.$delete(this.user.modules, project.id);
        },
        selectAllModules() {
            this.availableModules.forEach(project => {
                this.selectProjectModules(project);
            });
        },
        deselectAllModules() {
            this.availableModules.forEach(project => {
                this.deselectProjectModules(project);
            });
        },
        selectUser(user) {
            if (user) {
                this.user.id = user.id;
                this.user.name = user.name;
                this.user.additional_emails = user.additional_emails || [];
                this.isDataRoomUser = user.roles.some(role => {
                    ['user', 'organisation'].includes(role);
                });
            } else {
                this.user.id = undefined;
                this.user.name = null;
                this.user.additional_emails = [];
                this.isDataRoomUser = false;
            }
        },
        async resendInvite() {
            await (new User({})).sendInvitationEmail({
                id: this.user.id,
                project_id: this.context === 'project' ? ca.project.uuid : undefined,
                client_id: this.context === 'client' ? ca.client.uuid : undefined
            });

            ca.notify({
                type: 'success',
                message: 'Invite has been resent',
                timeout: ca.config('toast.timeout'),
            });
        },
        async resetPassword() {
            await (new User({})).sendResetEmail({
                id: this.user.id,
                project_id: this.context === 'project' ? ca.project.uuid : undefined
            });

            ca.notify({
                type: 'success',
                message: 'Password reset has been sent',
                timeout: ca.config('toast.timeout'),
            });
        },
        isProjectSelected(project) {
            if (!this.user.modules[project.id]) {
                return false;
            }
            return this.user.modules[project.id].length > 0;
        },
        setAdditionalEmailsAsValid(validity) {
            this.additionalEmailsAreValid = validity;
        },
        fieldHasErrors(field) {
            return has(this.errors, field);
        },
    },
    watch: {
        isValid(value) {
            this.$emit('validate', value);
        },
        user: {
            deep: true,
            handler(newValue) {
                if (has(newValue, 'role')) {
                    this.isDataRoomUser = ['user', 'organisation'].includes(newValue.role);
                }

                if (!this.canAssignRole && this.canAssignModules && !has(this.user, 'role')) {
                    this.user.role = 'staff';
                }
            }
        },
    }
}
</script>
